import { createAsyncThunk, createSlice, ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { apolloClient } from "../../apollo/graphql";
import { GET_ALL_COMPLAINTS, GET_COMPLAINT } from "../../apollo/queries/complaints";
import {
  CREATE_COMPLAINT,
  UPDATE_COMPLAINT,
  UPDATE_COMPLAINT_STATUS,
} from "../../apollo/mutations/complaints";
import { RejectWithValueType } from "../auth/types/authType";
import {
  Complaint,
  ComplaintArgs,
  ComplaintInput,
  ComplaintState,
  ComplaintsWithCount,
  UpdateComplaintStatusInput,
} from "./types/complaintTypes";

async function setErrorAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: { message: string },
) {
  dispatch(setError(payload));
  setTimeout(() => dispatch(resetError()), 6000);
}

async function setSuccessAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: { message: string; setupType?: string },
) {
  dispatch(setSuccess(payload));
  setTimeout(() => dispatch(resetSuccess()), 2000);
}

export const fetchComplaints = createAsyncThunk<
  ComplaintsWithCount,
  ComplaintArgs,
  { rejectValue: RejectWithValueType }
>("complaints/fetchComplaints", async (args, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_ALL_COMPLAINTS,
      variables: { ...args },
      fetchPolicy: "network-only",
    });
    if (response?.data?.findComplaints) {
      return response.data.findComplaints as ComplaintsWithCount;
    }
    return { totalCount: 0, complaints: [] };
  } catch (error: any) {
    const message = error?.message ?? "Failed to load complaints";
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const fetchComplaint = createAsyncThunk<
  Complaint,
  string,
  { rejectValue: RejectWithValueType }
>("complaints/fetchComplaint", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_COMPLAINT,
      variables: { id },
      fetchPolicy: "network-only",
    });
    if (response?.data?.getComplaint) {
      return response.data.getComplaint as Complaint;
    }
    throw new Error("Complaint not found");
  } catch (error: any) {
    const message = error?.message ?? "Failed to load complaint";
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const createComplaint = createAsyncThunk<
  Complaint,
  ComplaintInput,
  { rejectValue: RejectWithValueType }
>("complaints/createComplaint", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: CREATE_COMPLAINT,
      variables: { input },
    });
    if (response?.data?.createComplaint) {
      await setSuccessAction(dispatch, {
        message: "Complaint created successfully",
        setupType: "complaint",
      });
      return response.data.createComplaint as Complaint;
    }
    throw new Error("Failed to create complaint");
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ?? error?.message ?? "Create failed";
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const updateComplaint = createAsyncThunk<
  Complaint,
  ComplaintInput,
  { rejectValue: RejectWithValueType }
>("complaints/updateComplaint", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: UPDATE_COMPLAINT,
      variables: { input },
    });
    if (response?.data?.updateComplaint) {
      await setSuccessAction(dispatch, {
        message: "Complaint updated successfully",
        setupType: "complaint",
      });
      return response.data.updateComplaint as Complaint;
    }
    throw new Error("Failed to update complaint");
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ?? error?.message ?? "Update failed";
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const updateComplaintStatus = createAsyncThunk<
  Complaint,
  UpdateComplaintStatusInput,
  { rejectValue: RejectWithValueType }
>("complaints/updateComplaintStatus", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: UPDATE_COMPLAINT_STATUS,
      variables: { input },
    });
    if (response?.data?.updateComplaintStatus) {
      await setSuccessAction(dispatch, {
        message: "Complaint status updated",
        setupType: "complaint",
      });
      return response.data.updateComplaintStatus as Complaint;
    }
    throw new Error("Failed to update status");
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ??
      error?.message ??
      "Status update failed";
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

const initialState: ComplaintState = {
  complaintsWithCount: { totalCount: 0, complaints: [] },
  selectedComplaint: null,
  loading: "idle",
  success: null,
  error: null,
};

const complaintSlice = createSlice({
  name: "complaints",
  initialState,
  reducers: {
    setSuccess: (state, { payload }) => {
      state.success = payload;
    },
    resetSuccess: (state) => {
      state.success = null;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      if (payload.message?.includes("authorized")) {
        localStorage.removeItem("userInfo");
      }
    },
    resetError: (state) => {
      state.error = null;
    },
    resetSelectedComplaint: (state) => {
      state.selectedComplaint = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchComplaints.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchComplaints.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.complaintsWithCount = payload;
    });
    builder.addCase(fetchComplaints.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(fetchComplaint.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchComplaint.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedComplaint = payload;
    });
    builder.addCase(fetchComplaint.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(createComplaint.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(createComplaint.fulfilled, (state) => {
      state.loading = "idle";
    });
    builder.addCase(createComplaint.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(updateComplaint.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(updateComplaint.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedComplaint = payload;
    });
    builder.addCase(updateComplaint.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(updateComplaintStatus.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(updateComplaintStatus.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedComplaint = payload;
    });
    builder.addCase(updateComplaintStatus.rejected, (state) => {
      state.loading = "idle";
    });
  },
});

export const { setSuccess, resetSuccess, setError, resetError, resetSelectedComplaint } =
  complaintSlice.actions;

export default complaintSlice.reducer;

export const selectComplaints = (state: RootState) =>
  state.complaints as ComplaintState;
